/* eslint-disable @typescript-eslint/no-use-before-define */

import React, { FC } from 'react';

import styled from '@emotion/styled';
import { Icon as AntdIcon, Spin } from 'antd';
import { Icon, Icons } from 'src/shared/ds/components/Icon';
import {
    ComponentTextMedium,
    ComponentTextSmall,
    ComponentTextXSmall,
} from 'src/shared/ds/Typography';
import { dsColors } from 'src/shared/ds/variables';
import { pxToRem } from 'src/shared/helpers/pxToRem';
import { voidFunction } from 'src/shared/helpers/voidFunction';

export interface ButtonProps {
    title: string;
    onClick: () => void;
    rounded?: boolean;
    marginTop?: number;
    marginLeft?: number;
    startIcon?: Icons;
    size?: keyof typeof sizes;
    variant?: keyof typeof variants;
    completed?: boolean;
    fullWidth?: boolean;
    disabled?: boolean;
    testID?: string;
    loading?: boolean;
}

export const Button: FC<ButtonProps> = ({
    title,
    rounded,
    marginTop = 0,
    marginLeft = 0,
    onClick,
    startIcon,
    size = 'small',
    variant = 'primary',
    completed,
    fullWidth,
    disabled,
    testID,
    loading = false,
}) => {
    const { containerSize, Text } = sizes[size];
    const buttonVariant = { ...variants[variant], ...(completed && completedVariants[variant]) };

    return (
        <ButtonContainer
            onClick={loading || disabled ? voidFunction : onClick}
            disabled={disabled}
            css={[
                buttonVariant,
                rounded && styles.roundedContainer,
                { marginTop: pxToRem(marginTop ?? 0) },
                { marginLeft: pxToRem(marginLeft ?? 0) },
                containerSize,
                fullWidth && styles.fullWidth,
                disabled && styles.disabled,
            ]}
            data-testid={testID}
        >
            {loading && (
                <Spin
                    indicator={
                        <LoadingIcon
                            color={buttonVariant.color}
                            type="loading"
                            spin
                            data-testID={ButtonTestID.Loading}
                        />
                    }
                />
            )}
            <ButtonContent isLoading={loading}>
                {startIcon && (
                    <IconContainer>
                        <Icon name={startIcon} className="icon" color={buttonVariant.color} />
                    </IconContainer>
                )}
                <Text fontColor={buttonVariant.color}>{title}</Text>
            </ButtonContent>
        </ButtonContainer>
    );
};

const ButtonContainer = styled.button({
    display: 'flex',
    alignSelf: 'flex-start',
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: pxToRem(2),
    justifyContent: 'center',
    borderRadius: pxToRem(4),
    borderStyle: 'solid',
    position: 'relative',
});

const ButtonContent = styled.div<{ isLoading: boolean }>(({ isLoading }) => ({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    visibility: isLoading ? 'hidden' : 'visible',
}));

const IconContainer = styled.div({
    display: 'flex',
    '.icon': {
        marginRight: pxToRem(6),
    },
});

const LoadingIcon = styled(AntdIcon)<{ color: string }>(({ color }) => ({
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    color,
}));

const styles = {
    sizeSmall: {
        padding: `${pxToRem(6)} ${pxToRem(12)}`,
    },
    sizeMedium: {
        padding: `${pxToRem(8)} ${pxToRem(18)}`,
    },
    sizeLarge: {
        padding: `${pxToRem(14)} ${pxToRem(22)}`,
    },
    roundedContainer: {
        borderRadius: pxToRem(50),
    },
    fullWidth: {
        flex: 1,
    },
    disabled: {
        backgroundColor: dsColors.mediumGrey,
        borderColor: dsColors.mediumGrey,
        cursor: 'not-allowed',
    },
};

const variants = {
    primary: {
        borderColor: dsColors.blue500,
        backgroundColor: dsColors.blue500,
        color: dsColors.white,
    },
    secondary: {
        backgroundColor: dsColors.white,
        color: dsColors.neutral400,
        borderColor: dsColors.lightGrey,
    },
    flat: {
        backgroundColor: dsColors.white,
        color: dsColors.black,
        borderColor: dsColors.white,
    },
};

const completedVariants = {
    primary: {},
    flat: {},
    secondary: {
        borderColor: dsColors.white,
        color: dsColors.white,
        backgroundColor: 'transparent',
    },
};

const sizes = {
    small: {
        containerSize: styles.sizeSmall,
        Text: ComponentTextXSmall,
    },
    medium: {
        containerSize: styles.sizeMedium,
        Text: ComponentTextSmall,
    },
    large: {
        containerSize: styles.sizeLarge,
        Text: ComponentTextMedium,
    },
};

export enum ButtonTestID {
    Loading = 'ButtonTestID.Loading',
}
